import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Breadcrumb from "./Breadcrumb";
import { cities as allCities } from "../data/cities";
import { hasBlog, hasItinerary } from "../utils/contentAvailability";
import SEO from "./SEO";

/* ─── Google Map embed (no API key) ─── */
const GoogleMapEmbed = ({ place }) => {
  if (!place) return null;
  return (
    <div className="w-full h-[380px] rounded-3xl overflow-hidden border shadow-sm">
      <iframe
        title={`Map of ${place}`}
        src={`https://www.google.com/maps?q=${encodeURIComponent(place)}&output=embed`}
        className="w-full h-full"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
      />
    </div>
  );
};

export default function CityPage() {
  const { slug } = useParams();
  const [city, setCity]                   = useState(null);
  const [cityItinerary, setCityItinerary] = useState(null);
  const [relatedBlogs, setRelatedBlogs]   = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(false);
  const [selectedMapPlace, setSelectedMapPlace] = useState("");

  /* ── Explore More Cities: 4 random cities excluding current ── */
  const moreCities = useMemo(() => {
    const others = allCities.filter((c) => c.slug !== slug);
    /* deterministic shuffle using slug hash for SSR consistency */
    const seed = slug.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
    return others
      .map((c, i) => ({ c, sort: (seed * (i + 1)) % (others.length + 1) }))
      .sort((a, b) => a.sort - b.sort)
      .slice(0, 4)
      .map((x) => x.c);
  }, [slug]);

  /* ── Fetch City ── */
  useEffect(() => {
    setLoading(true);
    setError(false);
    fetch(`${process.env.REACT_APP_API_URL}/api/cities/${slug}`)
      .then((res) => {
        if (!res.ok) throw new Error("City not found");
        return res.json();
      })
      .then((data) => { setCity(data); setLoading(false); })
      .catch(() => { setError(true); setLoading(false); });
  }, [slug]);

  /* ── Fetch Itinerary ── */
  useEffect(() => {
    if (!slug) return;
    fetch(`${process.env.REACT_APP_API_URL}/api/itineraries/${slug}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => setCityItinerary(data))
      .catch(() => setCityItinerary(null));
  }, [slug]);

  /* ── Fetch Related Blogs (by slug keyword) ── */
  const API = process.env.REACT_APP_API_URL;
  useEffect(() => {
    if (!API) return;
    fetch(`${API}/api/blogs`)
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) return;
        // Filter blogs whose slug contains the city slug
        const related = data
          .filter((b) => b.slug && b.slug.includes(slug))
          .slice(0, 3);
        setRelatedBlogs(related);
      })
      .catch(() => setRelatedBlogs([]));
  }, [slug, API]);

  /* ── Map highlights (fail-safe cascade) ── */
  const mapHighlights = (() => {
    if (city?.mapHighlights?.length) return city.mapHighlights;
    if (cityItinerary?.highlight360Views?.length) return cityItinerary.highlight360Views;
    const fromDays = {};
    cityItinerary?.days?.forEach((day) => {
      day.places?.forEach((p) => {
        if (!fromDays[p]) fromDays[p] = { label: p.split(",")[0], place: p };
      });
    });
    if (Object.keys(fromDays).length > 0) return Object.values(fromDays).slice(0, 6);
    if (city?.neighborhoods?.length) {
      return city.neighborhoods.slice(0, 6).map((n) => ({
        label: n.name,
        place: `${n.name}, ${city.name}`,
      }));
    }
    if (city?.name) return [{ label: city.name, place: city.name }];
    return [];
  })();

  useEffect(() => {
    if (mapHighlights.length > 0) setSelectedMapPlace(mapHighlights[0].place);
  }, [mapHighlights]); // eslint-disable-line



  /* ── States ── */
  if (loading) return <div className="pt-40 text-center">Loading…</div>;
  if (error || !city) {
    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, follow" />
        </Helmet>
        <div className="pt-40 text-center text-red-500">City not found</div>
      </>
    );
  }

  return (
    <>
      <SEO 
        title={`${city.name} Travel Guide | Things to do in ${city.name}`}
        description={`Explore ${city.name}. ${city.tagline || ""} Plan your trip with AI using Expeditio.`}
        url={`/cities/${slug}`}
        image={city.heroImage}
      />
      <Helmet>
        <meta name="keywords" content={`${city.name} travel guide, things to do in ${city.name}, ${city.name} itinerary, visit ${city.name}`} />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TouristDestination",
          name: city.name,
          description: city.about || city.tagline,
          url: `https://www.expeditio.world/cities/${slug}`,
          image: city.heroImage,
          touristType: city.quickFacts?.bestFor?.map(b => ({ "@type": "Audience", audienceType: b })),
          includesAttraction: city.thingsToDo?.slice(0, 5).map(t => ({
            "@type": "TouristAttraction",
            name: t,
          })),
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home",   item: "https://www.expeditio.world/" },
            { "@type": "ListItem", position: 2, name: "Cities", item: "https://www.expeditio.world/cities" },
            { "@type": "ListItem", position: 3, name: city.name, item: `https://www.expeditio.world/cities/${slug}` },
          ],
        })}</script>
      </Helmet>

      <section className="bg-[#f7f9f8] pt-24 pb-28 px-4">
        <div className="max-w-6xl mx-auto space-y-16">

          {/* ── BREADCRUMB ── */}
          <Breadcrumb crumbs={[
            { label: "Home", to: "/" },
            { label: "Cities", to: "/cities" },
            { label: city.name },
          ]} />

          {/* ── BACK (now a Link) ── */}
          <Link
            to="/cities"
            className="text-sm text-gray-600 hover:text-[#5b7c67] hover:underline transition-colors"
          >
            ← Back to Cities
          </Link>

          {/* ── HERO ── */}
          <div className="relative">
            <img
              src={city.heroImage}
              alt={city.name}
              className="w-full h-[360px] object-cover rounded-[36px]"
            />
            <div className="absolute bottom-6 left-6 bg-white/90 backdrop-blur rounded-2xl px-6 py-4 shadow-lg">
              <h1 className="text-3xl font-semibold">{city.name}</h1>
              <p className="text-sm text-gray-600 mt-1">{city.tagline}</p>
            </div>
          </div>

          {/* ── QUICK FACTS ── */}
          <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Fact label="Timezone" value={city.quickFacts?.timezone} />
            <Fact label="Region" value={city.quickFacts?.region} />
            <Fact label="Budget" value={city.quickFacts?.budget} />
            <Fact label="Best For" value={city.quickFacts?.bestFor?.join(", ")} />
          </section>

          {/* ── ABOUT ── */}
          <Card title={`About ${city.name}`}>
            <p className="text-gray-700 leading-relaxed">{city.about}</p>
          </Card>

          {/* ── TRAVEL GUIDES (cross-link → blogs) ── */}
          <Card title="📖 Travel Guides">
            <p className="text-sm text-gray-500 mb-5">
              Dive deeper into {city.name} with curated blog articles.
            </p>
            <div className="flex flex-wrap gap-3">
              {/* Static guide link derived from slug */}
              {hasBlog(slug) && (
                <Link
                  to={`/blogs/${slug}-travel-guide`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#5b7c67]/10 text-[#5b7c67] text-sm font-semibold hover:bg-[#5b7c67]/20 transition-colors"
                >
                  {city.name} travel guide →
                </Link>
              )}
              {/* Any dynamically matched related blogs */}
              {relatedBlogs.map((b) => (
                <Link
                  key={b._id}
                  to={`/blogs/${b.slug}`}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 text-gray-700 text-sm font-medium hover:border-[#5b7c67] hover:text-[#5b7c67] transition-colors"
                >
                  {b.title} →
                </Link>
              ))}
              {!hasBlog(slug) && relatedBlogs.length === 0 && (
                <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gray-50 text-gray-500 text-sm font-semibold border border-gray-100">
                  Coming soon
                </span>
              )}
            </div>
          </Card>

          {/* ── MAP EXPLORE ── */}
          <Card title="📍 Explore on Map">
            <p className="text-sm text-gray-600 mb-4 max-w-2xl">
              Explore important locations and areas around {city.name}.
            </p>
            <div className="flex flex-wrap gap-3 mb-5">
              {mapHighlights.map(({ label, place }) => (
                <button
                  key={place}
                  onClick={() => setSelectedMapPlace(place)}
                  className={`px-4 py-2 rounded-full text-xs font-medium transition
                    ${selectedMapPlace === place
                      ? "bg-[#5b7c67] text-white shadow-md scale-105"
                      : "bg-white border hover:bg-gray-50 hover:scale-105"
                    }`}
                >
                  📍 {label}
                </button>
              ))}
            </div>
            <GoogleMapEmbed place={selectedMapPlace} />
          </Card>

          {/* ── BEST TIME ── */}
          <Card title="Best Time to Visit">
            <div className="grid sm:grid-cols-2 gap-4">
              {city.bestTime?.map((s, i) => (
                <div key={i} className="border rounded-2xl p-4">
                  <div className="font-medium">{s.season} · {s.months}</div>
                  <div className="text-sm text-gray-500">{s.temp}</div>
                  <p className="text-sm mt-2 text-gray-600">{s.desc}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card title="🌡️ Average Temperature">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-sm text-gray-500">Summer</div>
                <div className="text-lg font-semibold">{city.avgTemp?.summer || "—"}</div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="text-sm text-gray-500">Winter</div>
                <div className="text-lg font-semibold">{city.avgTemp?.winter || "—"}</div>
              </div>
            </div>
          </Card>

          <Card title="👥 Best For">
            <div className="space-y-3 text-sm text-gray-700">
              <p><strong>Couples:</strong> {city.bestForPeople?.couples}</p>
              <p><strong>Families:</strong> {city.bestForPeople?.families}</p>
              <p><strong>Friends:</strong> {city.bestForPeople?.friends}</p>
              <p><strong>Solo:</strong> {city.bestForPeople?.solo}</p>
            </div>
          </Card>

          <Card title="🏙️ Neighborhoods">
            <div className="grid sm:grid-cols-2 gap-4">
              {city.neighborhoods?.map((n, i) => (
                <div key={i} className="border rounded-2xl p-4">
                  <div className="font-medium">{n.name}</div>
                  <p className="text-sm text-gray-600 mt-1">{n.desc}</p>
                </div>
              ))}
            </div>
          </Card>

          <Card title="🚇 Transport">
            <div className="flex flex-wrap gap-2">
              {city.transport?.map((t, i) => (
                <span key={i} className="px-3 py-1 bg-gray-100 rounded-full text-sm">{t}</span>
              ))}
            </div>
          </Card>

          <Card title="🎯 Things To Do">
            <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
              {city.thingsToDo?.map((item, i) => <li key={i}>{item}</li>)}
            </ul>
          </Card>

          {/* ── NEARBY CITIES (now linked) ── */}
          <Card title="🧭 Nearby Cities">
            <div className="grid sm:grid-cols-2 gap-4">
              {city.nearbyCities?.map((c, i) => (
                <Link
                  key={i}
                  to={`/cities/${c.slug || c.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="block border rounded-xl p-4 hover:border-[#5b7c67] hover:shadow-sm transition-all"
                >
                  <div className="font-medium text-gray-800">{c.name}</div>
                  <div className="text-sm text-gray-500">{c.distance}</div>
                  <div className="mt-2 text-xs text-[#5b7c67] font-medium">Explore {c.name} →</div>
                </Link>
              ))}
            </div>
          </Card>

          {/* ── RECOMMENDED ITINERARY (linked) ── */}
          <Card title="Recommended Itinerary">
            {cityItinerary ? (
              <Link
                to={`/itineraries/${slug}`}
                className="block border rounded-2xl p-5 hover:shadow-md hover:border-[#5b7c67] transition-all"
              >
                <h3 className="text-lg font-semibold">{cityItinerary.title}</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {cityItinerary.duration} · {cityItinerary.difficulty}
                </p>
                <p className="text-sm text-gray-700 mt-2 line-clamp-2">{cityItinerary.description}</p>
                <div className="mt-3 text-sm text-[#5b7c67] font-medium">
                  View {city.name} itinerary →
                </div>
              </Link>
            ) : (
              <div className="space-y-3">
                <p className="text-sm text-gray-500">No itinerary available for this city yet.</p>
                <Link
                  to="/itineraries"
                  className="inline-flex items-center gap-1 text-sm text-[#5b7c67] font-medium hover:underline"
                >
                  Browse all itineraries →
                </Link>
              </div>
            )}
          </Card>

          {/* ── EXPLORE MORE CITIES ── */}
          {moreCities.length > 0 && (
            <Card title="🌍 Explore More Cities">
              <p className="text-sm text-gray-500 mb-5">
                Discover more destinations around the world.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {moreCities.map((c) => (
                  <Link
                    key={c.id || c.slug}
                    to={`/cities/${c.slug}`}
                    className="group block rounded-2xl overflow-hidden border border-gray-100 hover:border-[#5b7c67] hover:shadow-md transition-all"
                  >
                    <div className="h-24 overflow-hidden">
                      <img
                        src={c.image}
                        alt={`Things to do in ${c.name}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-3">
                      <div className="text-sm font-semibold text-gray-800 group-hover:text-[#5b7c67] transition-colors">{c.name}</div>
                      <div className="text-xs text-gray-400">{c.country}</div>
                      <div className="mt-1.5 text-xs text-[#5b7c67] font-medium">{c.name} guide →</div>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}

          {/* ── BOTTOM CROSS-LINKS ── */}
          <div className="flex flex-wrap gap-4 pt-4 border-t border-gray-200">
            {hasBlog(slug) && (
              <Link
                to={`/blogs/${slug}-travel-guide`}
                className="text-sm text-[#5b7c67] font-medium hover:underline underline-offset-2"
              >
                📖 Read the {city.name} travel guide
              </Link>
            )}
            {hasItinerary(slug) ? (
              <Link
                to={`/itineraries/${slug}`}
                className="text-sm text-[#5b7c67] font-medium hover:underline underline-offset-2"
              >
                🗓 View {city.name} itinerary
              </Link>
            ) : (
              <span className="text-sm text-gray-400 font-medium">
                🗓 Itinerary coming soon
              </span>
            )}
            <Link
              to="/cities"
              className="text-sm text-gray-500 hover:text-gray-700 hover:underline underline-offset-2"
            >
              ← All cities
            </Link>
          </div>

        </div>
      </section>
    </>
  );
}

/* ── UI Helpers ── */

function Card({ title, children }) {
  return (
    <section className="bg-white rounded-[32px] p-6 md:p-8 shadow-[0_10px_40px_rgba(0,0,0,0.05)]">
      <h2 className="text-xl font-semibold mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Fact({ label, value }) {
  return (
    <div className="bg-white rounded-2xl p-4 shadow-sm">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="font-medium text-sm mt-1">{value || "—"}</div>
    </div>
  );
}
